import React, { useState, useEffect, useMemo, useRef } from "react";
import VendorDataTable from "./VendorDataTable";
import useAssetProvider from "../useAssetProvider";
import NmefSpinner from "../../NmefSpinner";

import { makeStyles, withStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Toolbar from "@material-ui/core/Toolbar";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteIcon from "@material-ui/icons/Delete";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CloseIcon from "@material-ui/icons/Close";
import Alert from "@material-ui/lab/Alert";
import { checkEditAccess } from "../../Constants/utils";

const useStyles = makeStyles((theme) => ({
	root: {
		padding: "2px 4px",
		display: "flex",
		alignItems: "center",
		width: 400,
		boxShadow: "0 0 2px 0 rgba(0,0,0,.4)",
		borderRadius: 0,
		marginLeft: "10px",
	},
	dialogContent: {
		minHeight: "200px !important",
	},
	input: {
		marginLeft: theme.spacing(1),
		flex: 1,
	},
	searchBox: {
		display: "flex",
		alignItems: "center",
		marginBottom: "1rem",
	},
	toolbar: {
		justifyContent: "space-between",
		background: "rgb(232, 244, 253)",
		marginBottom: "0.5rem",
		minHeight: "auto !important",
	},
	inputText: {
		borderRadius: "0 !important",
		"& input": {
			paddingTop: "7.5px",
			paddingBottom: "7.5px",
		},
	},
	searchButton: {
		backgroundColor: "#2891E8",
		color: "#fff",
		textTransform: "capitalize",
		padding: "5px ​16px",
	},
	vendorBox: {
		borderRadius: 0,
		margin: 0,
		"& input": {
			padding: "8px 10px",
			fontSize: "0.875rem",
		},
		"& input:disabled": {
			color: "black !important", // (default alpha is 0.38)
		},
	},
}));

const styles = (theme) => ({
	root: {
		margin: 0,
		padding: theme.spacing(2),
		textAlign: "center",
		background: "rgb(46, 59, 85)",
		color: "white",
	},
	closeButton: {
		position: "absolute",
		right: theme.spacing(1),
		top: theme.spacing(1),
		color: "white",
	},
});

const DialogTitle = withStyles(styles)((props) => {
	const { children, classes, onClose, ...other } = props;
	return (
		<MuiDialogTitle disableTypography className={classes.root} {...other}>
			<Typography variant="h6">{children}</Typography>
			{onClose ? (
				<IconButton
					aria-label="close"
					className={classes.closeButton}
					onClick={onClose}>
					<CloseIcon />
				</IconButton>
			) : null}
		</MuiDialogTitle>
	);
});

const isSalesforceId = (value) =>
	/^001[0-9A-Za-z]{12}([0-9A-Za-z]{3})?$/.test(value.trim()); // 15 or 18 char Account Id

const isShortIdLike = (value) =>
	/^([A-Za-z]{2,3}-)?\d{4,}$/i.test(value.trim()); // "ACC-1234" or "1234"

const normalizeShortId = (value) => {
	const upper = value.trim().toUpperCase();
	return upper.startsWith("ACC-") ? upper : `ACC-${upper}`;
};

const normalizeText = (value) => {
	if (typeof value === "string") return value.trim();
	if (typeof value === "number") return value.toString();
	return "";
};

const buildAssetVendorList = (assets = [], fees = []) => {
	const entries = new Map();

	const coerceNumber = (value) => {
		const numeric = Number(value ?? 0);
		return Number.isFinite(numeric) ? numeric : 0;
	};

	const upsert = (candidate = {}) => {
		const shortId = normalizeText(candidate.short_id);
		const id = normalizeText(candidate.id);
		const name = normalizeText(candidate.name);
		const key = shortId || name || id;
		if (!key) return;

		const next = {
			id: id || shortId || name || key,
			short_id: shortId || id || name || key,
			name: name || shortId || id || key,
			number_of_fund: coerceNumber(candidate.number_of_fund),
			street: normalizeText(candidate.street),
			city: normalizeText(candidate.city),
			state: normalizeText(candidate.state),
			zip_code: normalizeText(candidate.zip_code),
			country: normalizeText(candidate.country),
		};

		const existing = entries.get(key);
		if (!existing) {
			entries.set(key, next);
			return;
		}

		const merged = { ...existing };
		if (!merged.id && next.id) merged.id = next.id;
		if (!merged.short_id && next.short_id) merged.short_id = next.short_id;
		if (!merged.name && next.name) merged.name = next.name;
		if (!merged.street && next.street) merged.street = next.street;
		if (!merged.city && next.city) merged.city = next.city;
		if (!merged.state && next.state) merged.state = next.state;
		if (!merged.zip_code && next.zip_code) merged.zip_code = next.zip_code;
		if (!merged.country && next.country) merged.country = next.country;
		merged.number_of_fund = Math.max(
			coerceNumber(existing.number_of_fund),
			next.number_of_fund
		);
		entries.set(key, merged);
	};

	const assetVendors =
		Array.isArray(assets) && assets.length
			? assets.map((asset = {}) => ({
				id:
					asset.vendor_id ??
					asset.vendor ??
					asset.master_vendor_id ??
					asset.vendor_name ??
					asset.master_vendor_name,
				short_id:
					asset.vendor_id ?? asset.vendor ?? asset.master_vendor_id ?? "",
				name:
					asset.vendor_name ?? asset.vendor ?? asset.master_vendor_name ?? "",
				number_of_fund:
					asset.number_of_fund ??
					asset.vendor_number_of_fund ??
					asset.funded_deals ??
					asset.vendor_funded_deals ??
					0,
				street: asset.vendor_street,
				city: asset.vendor_city,
				state: asset.vendor_state,
				zip_code: asset.vendor_zip || asset.vendor_zipcode,
				country: asset.vendor_country,
			}))
			: [];

	const feeVendors =
		Array.isArray(fees) && fees.length
			? fees.map((fee = {}) => ({
				id:
					fee.vendor_id ??
					fee.vendor_short_id ??
					fee.vendor ??
					fee.vendor_name,
				short_id:
					fee.vendor_id ?? fee.vendor_short_id ?? fee.vendor ?? "",
				name: fee.vendor_name ?? fee.vendor ?? "",
				number_of_fund: fee.number_of_fund ?? 0,
				street: fee.vendor_street,
				city: fee.vendor_city,
				state: fee.vendor_state,
				zip_code: fee.vendor_zipcode || fee.vendor_zip,
				country: fee.vendor_country,
			}))
			: [];

	[...assetVendors, ...feeVendors].forEach(upsert);

	return Array.from(entries.values());
};

const mapVendors = (vendors) => {
	if (!Array.isArray(vendors)) return [];
	return vendors.map((vendor) => {
		const addressParts = [
			vendor.street,
			vendor.city,
			vendor.state,
			vendor.zip_code,
		].filter(Boolean);
		return {
			id: vendor.id,
			short_id: vendor.short_id,
			name: vendor.name,
			number_of_fund: Number(vendor.number_of_fund ?? 0),
			address: addressParts.join(", "),
			street: vendor.street ?? "",
			city: vendor.city ?? "",
			state: vendor.state ?? "",
			zip_code: vendor.zip_code ?? "",
			country: vendor.country ?? "",
		};
	});
};

const formatVendorLabel = (vendor = {}) => {
	if (!vendor || !vendor.name) return "";
	return `${vendor.name}${vendor.address ? `, ${vendor.address}` : ""}`;
};

const dedupeVendorsById = (vendors) => {
	const mapById = new Map();
	for (const vendor of vendors) {
		if (vendor?.id) mapById.set(vendor.id, vendor);
	}
	return [...mapById.values()];
};

const sortVendorsByUsageThenShortIdDesc = (vendors) =>
	vendors.sort((vendorA, vendorB) => {
		const fundedDiff =
			Number(vendorB.number_of_fund ?? 0) -
			Number(vendorA.number_of_fund ?? 0);
		if (fundedDiff) return fundedDiff;
		return (vendorB.short_id || "").localeCompare(
			vendorA.short_id || "",
			undefined,
			{ numeric: true, sensitivity: "base" }
		);
	});

export default function VendorSearch({ notSupported, vendor, newAddedvendor, setVendor, isEdit, updateFeeVendor, type }) {
	const { assets, fees, getVendors, getVendor, isReadOnly } = useAssetProvider();
	const classes = useStyles();
	const searchRequestIdRef = useRef(0);
	const wasSearchingRef = useRef(false);
	const searchedActiveRef = useRef(false);

	const [loading, setLoading] = useState(false);
	const [vendorModal, setVendorModal] = useState(false);
	const [search, setSearch] = useState("");
	const [searchResult, setSearchResult] = useState([]);
	const [searchAttempted, setSearchAttempted] = useState(false);
	const [tableResetKey, setTableResetKey] = useState(0); // NEW: force table remount to reset pagination
	const vendorInfo = typeof vendor === "object" && vendor !== null ? vendor : {};
	const [selectedVendor, setSelectedVendor] = useState({
		id: vendorInfo.id || "",
		short_id: vendorInfo.short_id || vendorInfo.vendor || "",
		name: vendorInfo.name || vendorInfo.vendor_name || "",
		number_of_fund:
			Number(
				vendorInfo.number_of_fund ??
				vendorInfo.vendor_number_of_fund ??
				vendorInfo.funded_deals ??
				0
			) || 0,
		address: vendorInfo.address || "",
		street: vendorInfo.street || vendorInfo.billing_street || "",
		city: vendorInfo.city || vendorInfo.billing_city || "",
		state: vendorInfo.state || vendorInfo.billing_state || "",
		zip_code: vendorInfo.zip_code || vendorInfo.billing_postal_code || "",
		country: vendorInfo.country || vendorInfo.billing_country || "",
	});
	const trimmedSearch = useMemo(() => search.trim(), [search]);
	const hasSearchTerm = trimmedSearch.length > 0;
	const isShortQuery = hasSearchTerm && trimmedSearch.length < 3;
	const searchHelperText = isShortQuery ? "Please Enter at least 3 characters." : "";
	useEffect(() => {
		setVendor(selectedVendor);
	}, [selectedVendor]);

	const dealVendorList = useMemo(() => {
		const combined = buildAssetVendorList(assets, fees);
		return sortVendorsByUsageThenShortIdDesc(combined);
	}, [assets, fees]);

	const defaultVendorOptions = useMemo(() => mapVendors(dealVendorList), [dealVendorList]);
	const showNoResultsAlert = !loading && searchAttempted && searchResult.length === 0;
	const vendorDisplayValue = useMemo(() => {
		if (newAddedvendor?.id) {
			return formatVendorLabel(newAddedvendor);
		}
		return formatVendorLabel(selectedVendor);
	}, [newAddedvendor, selectedVendor]);
	const isVendorPickerDisabled =
		isReadOnly || notSupported || !checkEditAccess() || (!isEdit && type === "fee");

	const resetVendorSearchState = () => {
		searchRequestIdRef.current += 1;
		setLoading(false);
		setSearch("");
		setSearchResult(defaultVendorOptions);
		setTableResetKey((k) => k + 1); // NEW: reset pagination when returning to base list
		setSearchAttempted(false);
		searchedActiveRef.current = false;
	};

	useEffect(() => {
		if (hasSearchTerm) return; // still typing; don't touch pagination
		setSearchResult(defaultVendorOptions); // show default list
		// Only reset to page 1 if we were actually viewing searched results
		if (searchedActiveRef.current) {
			setTableResetKey((k) => k + 1);
			searchedActiveRef.current = false; // we're back to default context
		}

		// now it's empty → show default list
		setSearchResult(defaultVendorOptions);
		// reset pagination only if we were previously searching
		if (wasSearchingRef.current) {
			setTableResetKey((k) => k + 1);
		}
		setSearchAttempted(false);
		wasSearchingRef.current = false;
	}, [defaultVendorOptions, hasSearchTerm]);

	const handleSearchChange = (event) => {
		setSearch(event.target.value);
	};

	const handleClickOpen = () => {
		setVendorModal(true);
	};

	const handleClose = () => {
		setVendorModal(false);
		resetVendorSearchState();
	};

	const handleSubmit = async () => {
		if (!hasSearchTerm) {
			return;
		}

		const trimmedQuery = trimmedSearch;
		const requestId = searchRequestIdRef.current + 1;
		searchRequestIdRef.current = requestId;
		setSearchAttempted(true);
		setLoading(true);

		try {
			const looksLikeId =
				isSalesforceId(trimmedQuery) || isShortIdLike(trimmedQuery);
			const candidateVendorId = isShortIdLike(trimmedQuery)
				? normalizeShortId(trimmedQuery)
				: trimmedQuery;

			const [nameQuery, idQuery] = await Promise.allSettled([
				getVendors(trimmedQuery, 0), // GET vendors?name=<query>&Offset=0
				looksLikeId ? getVendor(candidateVendorId) : Promise.resolve(null), // GET vendor/<id>
			]);

			const vendorsByName =
				nameQuery.status === "fulfilled" && Array.isArray(nameQuery.value)
					? nameQuery.value
					: [];

			const vendorById =
				idQuery.status === "fulfilled" && idQuery.value && idQuery.value.id
					? [idQuery.value]
					: [];

			const merged = dedupeVendorsById([...vendorsByName, ...vendorById]);
			const sorted = sortVendorsByUsageThenShortIdDesc(merged);

			if (requestId !== searchRequestIdRef.current) return;
			const mappedResults = mapVendors(sorted);
			setSearchResult(mappedResults);
			setTableResetKey((k) => k + 1); // NEW: new search results → reset to page 0
			searchedActiveRef.current = true
		} catch {
			if (requestId === searchRequestIdRef.current) {
				setSearchResult([]);
				setTableResetKey((k) => k + 1); // NEW: also reset on empty to avoid stale page
				searchedActiveRef.current = true
			}
		} finally {
			if (requestId === searchRequestIdRef.current) {
				setLoading(false);
			}
		}
	};

	const removeVendor = () => {
		setSelectedVendor({
			id: "",
			short_id: "",
			name: "",
			number_of_fund: 0,
			address: "",
			street: "",
			city: "",
			state: "",
			zip_code: "",
			country: "",
		});
	};

	return (
		<Box>
			<TextField
				fullWidth
				className={classes.vendorBox}
				variant="outlined"
				size="small"
				disabled
				value={vendorDisplayValue}
				InputProps={{
					endAdornment: (
						<InputAdornment position="end">
							<IconButton
								disabled={isVendorPickerDisabled}
								size="small"
								onClick={handleClickOpen}>
								<ExpandMoreIcon />
							</IconButton>
						</InputAdornment>
					),
				}}
			/>
			<Dialog
				maxWidth="md"
				open={vendorModal}
				onClose={handleClose}
				aria-labelledby="vendor-dialog-title">
				<DialogTitle id="customized-dialog-title" onClose={handleClose}>
					Vendors
				</DialogTitle>
				<DialogContent className={classes.dialogContent} dividers>
					<Box style={{ marginBottom: "0.5rem" }}>
						<TextField
							error={isShortQuery}
							value={search}
							onChange={handleSearchChange}
							label="Vendor search"
							size="small"
							variant="outlined"
							placeholder="Enter vendor name or part of vendor name"
							helperText={searchHelperText}
							InputLabelProps={{ shrink: true }}
							InputProps={{ className: classes.inputText }}
							style={{ width: "400px", margin: 0 }}
						/>{" "}
						&nbsp;&nbsp;
						<Button
							variant="contained"
							disableElevation={true}
							onClick={handleSubmit}
							disabled={isShortQuery || !hasSearchTerm}
							className={classes.searchButton}>
							Search
						</Button>
						<Button
							variant="contained"
							disableElevation={true}
							onClick={handleClose}
							style={{ marginLeft: "5px", marginRight: "5px" }}
							className={classes.searchButton}>
							Done
						</Button>
					</Box>
					<Box>
						{selectedVendor.id !== "" && selectedVendor.id !== undefined && (
							<Toolbar className={classes.toolbar}>
								<Typography
									className={classes.title}
									color="inherit"
									variant="subtitle1">
									{selectedVendor.name} is selected.
								</Typography>
								<Tooltip title="Delete">
									<IconButton aria-label="delete" onClick={removeVendor}>
										<DeleteIcon color="error" />
									</IconButton>
								</Tooltip>
							</Toolbar>
						)}
					</Box>

					{loading && <NmefSpinner />}
					{showNoResultsAlert && (
						<Alert severity="error" style={{ marginBottom: "0.5rem" }}>
							No records found! Please search again.
						</Alert>
					)}

					{!loading && searchResult.length > 0 && (
						<VendorDataTable
							key={`vendors-${tableResetKey}`}
							data={searchResult}
							selectedVendor={selectedVendor}
							setSelectedVendor={setSelectedVendor}
							type={type}
							updateFeeVendor={updateFeeVendor}
						/>
					)}
				</DialogContent>
			</Dialog>
		</Box>
	);
}
